import { useState } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { Sheet, SheetContent, SheetTrigger } from "@/components/ui/sheet";
import { SignOut, Gear, ChartBar, CaretLeft, BookOpen, List, UserCircle } from "@phosphor-icons/react";

export function Logo({ className = "", dark = false, height = 26 }) {
  return (
    <img
      src={dark ? "/selectflow-logo-dark.svg" : "/selectflow-logo.svg"}
      alt="SelectFlow"
      style={{ height }}
      className={`w-auto ${className}`}
    />
  );
}

const NAV = [
  { to: "/app", label: "Elections", icon: ChartBar, match: (p) => p === "/app" || p.startsWith("/app/elections") },
  { to: "/app/settings", label: "Settings", icon: Gear, match: (p) => p.startsWith("/app/settings") },
  { to: "/app/guide", label: "Guide", icon: BookOpen, match: (p) => p.startsWith("/app/guide") },
];

function NavContent({ onNavigate }) {
  const { user, org, logout } = useAuth();
  const nav = useNavigate();
  const loc = useLocation();
  return (
    <div className="flex flex-col h-full">
      <div className="px-5 pt-6 pb-5">
        <Link to="/app" onClick={onNavigate} className="inline-flex"><Logo /></Link>
        {org?.name && (
          <div className="mt-4 flex items-center gap-2 rounded-lg bg-slate-50 border border-slate-200 px-3 py-2">
            <div className="w-6 h-6 rounded-md bg-[#1F6FE5] text-white flex items-center justify-center text-xs font-semibold shrink-0">
              {org.name.charAt(0).toUpperCase()}
            </div>
            <span className="text-sm font-medium text-slate-700 truncate" data-testid="sidebar-org-name">{org.name}</span>
          </div>
        )}
      </div>

      <nav className="flex-1 px-3 space-y-1">
        {NAV.map((item) => {
          const active = item.match(loc.pathname);
          const Icon = item.icon;
          return (
            <Link
              key={item.to}
              to={item.to}
              onClick={onNavigate}
              data-testid={`sidebar-nav-${item.label.toLowerCase()}`}
              className={`flex items-center gap-3 rounded-lg px-3 py-2.5 text-sm transition-colors ${
                active ? "bg-[#EAF1FD] text-[#1F6FE5] font-semibold" : "text-slate-600 hover:bg-slate-50 hover:text-slate-900 font-medium"
              }`}
            >
              <Icon size={20} weight={active ? "fill" : "regular"} />
              {item.label}
            </Link>
          );
        })}
      </nav>

      <div className="px-3 pb-5 pt-3 border-t border-slate-200 mt-3">
        <Link
          to="/app/profile"
          onClick={onNavigate}
          data-testid="sidebar-nav-profile"
          className={`flex items-center gap-3 rounded-lg px-3 py-2.5 transition-colors ${
            loc.pathname.startsWith("/app/profile") ? "bg-[#EAF1FD]" : "hover:bg-slate-50"
          }`}
        >
          <UserCircle size={30} weight="duotone" className="text-slate-400" />
          <div className="min-w-0">
            <div className="text-sm font-medium text-slate-800 truncate">{user?.name}</div>
            <div className="text-[10px] uppercase tracking-widest text-slate-400">{user?.role}</div>
          </div>
        </Link>
        <button
          data-testid="logout-btn"
          onClick={() => { logout(); nav("/"); }}
          className="mt-1 w-full flex items-center gap-3 rounded-lg px-3 py-2.5 text-sm font-medium text-slate-500 hover:bg-red-50 hover:text-red-600 transition-colors"
        >
          <SignOut size={20} weight="bold" /> Sign out
        </button>
      </div>
    </div>
  );
}

export default function Layout({ children, back }) {
  const { user } = useAuth();
  const nav = useNavigate();
  const [mobileOpen, setMobileOpen] = useState(false);

  return (
    <div className="min-h-screen bg-[#F7F8FA]">
      {/* Desktop sidebar */}
      <aside className="hidden md:flex flex-col fixed inset-y-0 left-0 w-60 bg-white border-r border-slate-200 z-30" data-testid="app-sidebar">
        <NavContent />
      </aside>

      <div className="md:pl-60">
        {/* Mobile top bar */}
        <div className="md:hidden sticky top-0 z-20 flex items-center justify-between h-14 px-4 bg-white/90 backdrop-blur-md border-b border-slate-200">
          <Sheet open={mobileOpen} onOpenChange={setMobileOpen}>
            <SheetTrigger asChild>
              <button data-testid="mobile-menu-btn" className="text-slate-600 hover:text-slate-900 p-1"><List size={24} weight="bold" /></button>
            </SheetTrigger>
            <SheetContent side="left" className="p-0 w-64 bg-white">
              <NavContent onNavigate={() => setMobileOpen(false)} />
            </SheetContent>
          </Sheet>
          <Link to="/app"><Logo height={22} /></Link>
          <span className="w-6" />
        </div>

        {user?.demo_viewer && (
          <div data-testid="demo-banner" className="bg-[#1F6FE5] text-white text-sm">
            <div className="max-w-[1200px] mx-auto px-5 sm:px-8 py-2.5 flex flex-wrap items-center justify-between gap-3">
              <span className="font-medium">You're exploring a read-only live demo. Changes are disabled.</span>
              <Link to="/auth?mode=register" data-testid="demo-banner-cta" className="font-semibold underline underline-offset-2 hover:opacity-90">Start free to run your own →</Link>
            </div>
          </div>
        )}

        <main className="max-w-[1200px] mx-auto px-5 sm:px-8 py-8">
          {back && (
            <button
              data-testid="back-btn"
              onClick={() => nav(back)}
              className="mb-5 text-slate-500 hover:text-slate-900 transition-colors inline-flex items-center gap-1 text-sm"
            >
              <CaretLeft size={16} weight="bold" /> Back
            </button>
          )}
          {children}
        </main>
      </div>
    </div>
  );
}
